/**
 * APK Configuration Panel
 * 
 * Admin panel for configuring APK download URL and related settings
 * Allows admins to update the APK download link and version info
 */

import React, { useState, useEffect } from 'react';
import {
    Smartphone,
    Download,
    Link,
    Save,
    RefreshCw,
    AlertTriangle,
    CheckCircle,
    Upload,
    FileText,
    Bell,
    Zap,
    Package,
    Globe,
    Shield,
    Info,
    Copy,
    ExternalLink
} from 'lucide-react';

export interface APKConfig {
    downloadUrl: string;
    googleDriveFolderId?: string; // NEW: Folder ID for auto-fetching latest APK
    googleDriveApiKey?: string; // Optional API key for better rate limits
    autoFetchLatest?: boolean; // Enable auto-fetch from folder
    version: string;
    versionCode: number;
    releaseDate: string;
    fileSize: string;
    minAndroidVersion: string;
    changelog: string[];
    isEnabled: boolean;
    forceUpdate: boolean;
    updateMessage: string;
    // Feature flags for APK
    features: {
        pushNotifications: boolean;
        hapticFeedback: boolean;
        offlineMode: boolean;
        biometricAuth: boolean;
        backgroundSync: boolean;
        deepLinking: boolean;
    };
}

export const DEFAULT_APK_CONFIG: APKConfig = {
    downloadUrl: '',
    googleDriveFolderId: '',
    googleDriveApiKey: '',
    autoFetchLatest: true,
    version: '3.0.0',
    versionCode: 30,
    releaseDate: new Date().toISOString().split('T')[0],
    fileSize: '~25MB',
    minAndroidVersion: '7.0 (Nougat)',
    changelog: [
        '60fps smooth performance',
        'Push notifications for study reminders',
        'Haptic feedback on interactions',
        'Offline mode support',
        'Native sharing capabilities',
    ],
    isEnabled: true,
    forceUpdate: false,
    updateMessage: 'A new version of Luca is available! Update now for the best experience.',
    features: {
        pushNotifications: true,
        hapticFeedback: true,
        offlineMode: true,
        biometricAuth: false,
        backgroundSync: true,
        deepLinking: true,
    },
};

interface APKConfigPanelProps {
    onSave?: (config: APKConfig) => void;
}

const APKConfigPanel: React.FC<APKConfigPanelProps> = ({ onSave }) => {
    const [config, setConfig] = useState<APKConfig>(DEFAULT_APK_CONFIG);
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [newChangelogItem, setNewChangelogItem] = useState('');

    // Load saved config and listen to real-time updates
    useEffect(() => {
        // 1. Load initial state from localStorage (Fast render)
        const savedConfig = localStorage.getItem('luca_apk_config');
        if (savedConfig) {
            try {
                const parsed = JSON.parse(savedConfig);
                setConfig(prev => ({ ...DEFAULT_APK_CONFIG, ...parsed }));
            } catch (e) {
                console.error('[APKConfig] Failed to parse saved config');
            }
        }

        // 2. Fetch latest from REST API (Reliable fallback)
        const fetchLatest = async () => {
            try {
                console.log('[APKConfig] Fetching latest config from REST API...');
                const latest = await getAPKConfigFromFirebase();
                if (latest && latest.version) {
                    console.log('[APKConfig] ✅ REST API returned version:', latest.version);
                    setConfig(prev => {
                        const newConfig = { ...prev, ...latest };
                        localStorage.setItem('luca_apk_config', JSON.stringify(newConfig));
                        return newConfig;
                    });
                }
            } catch (error) {
                console.error('[APKConfig] Failed to fetch from REST API:', error);
            }
        };
        fetchLatest();

        // 3. Setup real-time listener (Live updates)
        let unsubscribe = () => { };

        const setupListener = async () => {
            try {
                const { getFirestore, doc, onSnapshot } = await import('firebase/firestore');
                const { initializeApp, getApps } = await import('firebase/app');

                const firebaseConfig = {
                    apiKey: "AIzaSyDfI0ey0RYiNcQm9LBXRoJgP5rbPah6FtI",
                    authDomain: "luca-dffcd.firebaseapp.com",
                    projectId: "luca-dffcd",
                    storageBucket: "luca-dffcd.firebasestorage.app",
                    messagingSenderId: "249847474320",
                    appId: "1:249847474320:web:93929ed662f81a7ca34eda",
                    measurementId: "G-K59VY1QJ51"
                };

                const apps = getApps();
                const app = apps.length === 0 ? initializeApp(firebaseConfig) : apps[0];
                const db = getFirestore(app);

                unsubscribe = onSnapshot(doc(db, 'config', 'apkVersion'), (docSnap) => {
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        console.log('[APKConfig] 🔄 Received real-time update:', data);

                        setConfig(prev => {
                            // Only update if data is different to avoid loops
                            if (prev.version === data.version && prev.downloadUrl === data.downloadUrl) {
                                return prev;
                            }

                            const newConfig = {
                                ...prev,
                                version: data.version || prev.version,
                                versionCode: data.versionCode || prev.versionCode,
                                downloadUrl: data.downloadUrl || prev.downloadUrl,
                                forceUpdate: data.forceUpdate ?? prev.forceUpdate,
                                updateMessage: data.updateMessage || prev.updateMessage,
                                releaseDate: data.releaseDate || prev.releaseDate,
                                fileSize: data.fileSize || prev.fileSize,
                                changelog: data.changelog || prev.changelog,
                                isEnabled: data.isEnabled ?? prev.isEnabled,
                            };

                            // Update localStorage to keep it in sync
                            localStorage.setItem('luca_apk_config', JSON.stringify(newConfig));

                            return newConfig;
                        });
                    }
                }, (error) => {
                    console.error('[APKConfig] Listener error:', error);
                });
            } catch (error) {
                console.error('[APKConfig] Failed to setup listener:', error);
            }
        };

        setupListener();

        return () => unsubscribe();
    }, []);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // Save to localStorage
            localStorage.setItem('luca_apk_config', JSON.stringify(config));

            // Sync to Firebase for auto-updater
            await syncToFirebase(config);

            if (onSave) {
                await onSave(config);
            }
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error) {
            console.error('[APKConfig] Save error:', error);
        }
        setIsSaving(false);
    };

    /**
     * Sync APK config to Firebase for auto-updater
     */
    const syncToFirebase = async (apkConfig: APKConfig) => {
        try {
            console.log('[APKConfig] Syncing to Firebase...', apkConfig.version);

            // Import Firebase dynamically to avoid circular dependencies
            const { getFirestore, doc, setDoc } = await import('firebase/firestore');
            const { initializeApp, getApps } = await import('firebase/app');

            // Initialize Firebase if not already initialized
            const firebaseConfig = {
                apiKey: "AIzaSyDfI0ey0RYiNcQm9LBXRoJgP5rbPah6FtI",
                authDomain: "luca-dffcd.firebaseapp.com",
                projectId: "luca-dffcd",
                storageBucket: "luca-dffcd.firebasestorage.app",
                messagingSenderId: "249847474320",
                appId: "1:249847474320:web:93929ed662f81a7ca34eda",
                measurementId: "G-K59VY1QJ51"
            };

            const apps = getApps();
            const app = apps.length === 0 ? initializeApp(firebaseConfig) : apps[0];
            const db = getFirestore(app);

            // Create/update the apkVersion document
            const versionDocRef = doc(db, 'config', 'apkVersion');
            await setDoc(versionDocRef, {
                version: apkConfig.version,
                versionCode: apkConfig.versionCode,
                downloadUrl: apkConfig.downloadUrl,
                forceUpdate: apkConfig.forceUpdate,
                changelog: apkConfig.changelog,
                updateMessage: apkConfig.updateMessage,
                releaseDate: apkConfig.releaseDate,
                fileSize: apkConfig.fileSize,
                lastUpdated: new Date().toISOString(),
                updatedBy: 'admin'
            });

            console.log('[APKConfig] ✅ Successfully synced to Firebase');
        } catch (error) {
            console.error('[APKConfig] ❌ Failed to sync to Firebase:', error);
            // Don't throw - localStorage save should still succeed
        }
    };

    const addChangelogItem = () => {
        if (newChangelogItem.trim()) {
            setConfig(prev => ({
                ...prev,
                changelog: [...prev.changelog, newChangelogItem.trim()],
            }));
            setNewChangelogItem('');
        }
    };

    const removeChangelogItem = (index: number) => {
        setConfig(prev => ({
            ...prev,
            changelog: prev.changelog.filter((_, i) => i !== index),
        }));
    };

    const copyDownloadUrl = () => {
        if (config.downloadUrl) {
            navigator.clipboard.writeText(config.downloadUrl);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl text-white">
                            <Smartphone size={24} />
                        </div>
                        APK Configuration
                    </h2>
                    <p className="text-slate-500 mt-1">Configure Android APK download and update settings</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => {
                            if (confirm('Clear all caches and refresh? This will force-reload the app.')) {
                                clearAllCaches().then(() => performCacheBustedRefresh());
                            }
                        }}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
                        title="Clear all caches and refresh app"
                    >
                        <RefreshCw size={18} />
                        <span className="hidden sm:inline">Clear Cache</span>
                    </button>

                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-all ${saveSuccess
                            ? 'bg-emerald-500 text-white'
                            : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:shadow-lg hover:-translate-y-0.5'
                            }`}
                    >
                        {isSaving ? (
                            <RefreshCw size={18} className="animate-spin" />
                        ) : saveSuccess ? (
                            <CheckCircle size={18} />
                        ) : (
                            <Save size={18} />
                        )}
                        {isSaving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Changes'}
                    </button>
                </div>
            </div>

            {/* Main Configuration */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Download URL Card */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                    <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-4">
                        <Link size={18} className="text-blue-500" />
                        APK Download URL
                    </h3>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                Download URL *
                            </label>
                            <div className="relative">
                                <input
                                    type="url"
                                    value={config.downloadUrl}
                                    onChange={(e) => setConfig(prev => ({ ...prev, downloadUrl: e.target.value }))}
                                    placeholder="https://your-server.com/luca-cpa.apk"
                                    className="w-full px-4 py-2.5 pr-20 bg-slate-50 border border-slate-200 rounded-xl 
                                        focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                                />
                                {config.downloadUrl && (
                                    <button
                                        onClick={copyDownloadUrl}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-slate-600"
                                        title="Copy URL"
                                    >
                                        <Copy size={16} />
                                    </button>
                                )}
                            </div>
                            <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
                                <Info size={12} />
                                Direct link to the APK file (Google Drive, S3, or your server)
                            </p>
                        </div>

                        {/* Auto-Fetch from Google Drive Folder */}
                        <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl space-y-4">
                            <div className="flex items-start gap-2">
                                <Zap size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                    <p className="text-sm font-bold text-emerald-900 mb-1">🚀 Auto-Fetch Latest APK (Recommended)</p>
                                    <p className="text-xs text-emerald-700 mb-3">
                                        Automatically download the latest APK from your Google Drive folder - no manual URL updates needed!
                                    </p>

                                    {/* Enable Toggle */}
                                    <div className="flex items-center gap-3 mb-3">
                                        <input
                                            type="checkbox"
                                            checked={config.autoFetchLatest || false}
                                            onChange={(e) => setConfig(prev => ({ ...prev, autoFetchLatest: e.target.checked }))}
                                            className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                                        />
                                        <label className="text-sm font-medium text-emerald-900">
                                            Enable Auto-Fetch (Always get latest version)
                                        </label>
                                    </div>

                                    {/* Folder ID Input */}
                                    <div className="space-y-2">
                                        <label className="block text-xs font-semibold text-emerald-900">
                                            Google Drive Folder ID or URL
                                        </label>
                                        <input
                                            type="text"
                                            value={config.googleDriveFolderId || ''}
                                            onChange={(e) => setConfig(prev => ({ ...prev, googleDriveFolderId: e.target.value }))}
                                            placeholder="1abc123xyz or https://drive.google.com/drive/folders/1abc123xyz"
                                            className="w-full px-3 py-2 border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                                        />
                                        <p className="text-xs text-emerald-700">
                                            📁 Paste your folder ID or full folder URL here
                                        </p>
                                    </div>

                                    {/* API Key (Optional) */}
                                    <div className="space-y-2 mt-3">
                                        <label className="block text-xs font-semibold text-emerald-900">
                                            Google Drive API Key (Optional)
                                        </label>
                                        <input
                                            type="text"
                                            value={config.googleDriveApiKey || ''}
                                            onChange={(e) => setConfig(prev => ({ ...prev, googleDriveApiKey: e.target.value }))}
                                            placeholder="AIza... (optional, for higher rate limits)"
                                            className="w-full px-3 py-2 border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                                        />
                                        <p className="text-xs text-emerald-700">
                                            🔑 Optional: Add an API key for better performance
                                        </p>
                                    </div>

                                    {/* Setup Instructions */}
                                    <div className="mt-3 p-3 bg-emerald-100/50 rounded-lg">
                                        <p className="text-xs font-semibold text-emerald-900 mb-2">Setup Steps:</p>
                                        <ol className="text-xs text-emerald-800 space-y-1 list-decimal list-inside">
                                            <li>Create a folder in Google Drive</li>
                                            <li>Upload your APK files to this folder</li>
                                            <li>Right-click folder → Share → "Anyone with the link"</li>
                                            <li>Copy the folder ID from the URL (after /folders/)</li>
                                            <li>Paste it above and enable auto-fetch ✅</li>
                                        </ol>
                                        <p className="text-xs text-emerald-700 mt-2 font-medium">
                                            💡 The system will automatically download the most recent APK!
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Manual URL Setup (Fallback) */}
                        <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl space-y-3">
                            <div className="flex items-start gap-2">
                                <Globe size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-blue-900 mb-2">Manual URL Setup (Fallback)</p>
                                    <p className="text-xs text-blue-700 mb-3">
                                        If auto-fetch is disabled, the system will use this URL instead.
                                    </p>
                                    <ol className="text-xs text-blue-800 space-y-1.5 list-decimal list-inside">
                                        <li>Upload your APK to Google Drive</li>
                                        <li>Right-click the file → Share → Change to "Anyone with the link"</li>
                                        <li>Copy the file ID from the share link (between /d/ and /view)</li>
                                        <li>Use this format: <code className="px-1 py-0.5 bg-blue-100 rounded text-blue-900">https://drive.google.com/uc?export=download&id=YOUR_FILE_ID</code></li>
                                    </ol>
                                    <p className="text-xs text-blue-700 mt-2 italic">
                                        Example: If link is drive.google.com/file/d/<strong>1abc123xyz</strong>/view,
                                        use: drive.google.com/uc?export=download&id=<strong>1abc123xyz</strong>
                                    </p>
                                </div>
                            </div>
                        </div>

                        {!config.downloadUrl && (
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                                <div className="flex items-center gap-2 text-amber-800 text-sm font-medium">
                                    <AlertTriangle size={16} />
                                    APK download URL not configured
                                </div>
                                <p className="text-xs text-amber-700 mt-1">
                                    Users won't be able to download the APK until you set a valid URL.
                                </p>
                            </div>
                        )}

                        {config.downloadUrl && (
                            <a
                                href={config.downloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                            >
                                <ExternalLink size={14} />
                                Test download link
                            </a>
                        )}
                    </div>
                </div>

                {/* Version Info Card */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                    <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-4">
                        <Package size={18} className="text-purple-500" />
                        Version Information
                    </h3>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                Version Name
                            </label>
                            <input
                                type="text"
                                value={config.version}
                                onChange={(e) => setConfig(prev => ({ ...prev, version: e.target.value }))}
                                placeholder="3.0.0"
                                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl 
                                    focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                Version Code
                            </label>
                            <input
                                type="number"
                                value={config.versionCode}
                                onChange={(e) => setConfig(prev => ({ ...prev, versionCode: parseInt(e.target.value) || 0 }))}
                                placeholder="30"
                                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl 
                                    focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                Release Date
                            </label>
                            <input
                                type="date"
                                value={config.releaseDate}
                                onChange={(e) => setConfig(prev => ({ ...prev, releaseDate: e.target.value }))}
                                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl 
                                    focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                File Size
                            </label>
                            <input
                                type="text"
                                value={config.fileSize}
                                onChange={(e) => setConfig(prev => ({ ...prev, fileSize: e.target.value }))}
                                placeholder="~25MB"
                                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl 
                                    focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-sm"
                            />
                        </div>
                    </div>
                </div>

                {/* Update Settings Card */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                    <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-4">
                        <RefreshCw size={18} className="text-amber-500" />
                        Update Settings
                    </h3>

                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                            <div>
                                <p className="font-medium text-slate-900 text-sm">Enable APK Downloads</p>
                                <p className="text-xs text-slate-500">Show install button to users</p>
                            </div>
                            <button
                                onClick={() => setConfig(prev => ({ ...prev, isEnabled: !prev.isEnabled }))}
                                className={`relative w-12 h-7 rounded-full transition-colors ${config.isEnabled ? 'bg-emerald-500' : 'bg-slate-300'
                                    }`}
                            >
                                <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${config.isEnabled ? 'translate-x-6' : 'translate-x-1'
                                    }`} />
                            </button>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                            <div>
                                <p className="font-medium text-slate-900 text-sm">Force Update</p>
                                <p className="text-xs text-slate-500">Require users to update before using</p>
                            </div>
                            <button
                                onClick={() => setConfig(prev => ({ ...prev, forceUpdate: !prev.forceUpdate }))}
                                className={`relative w-12 h-7 rounded-full transition-colors ${config.forceUpdate ? 'bg-red-500' : 'bg-slate-300'
                                    }`}
                            >
                                <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${config.forceUpdate ? 'translate-x-6' : 'translate-x-1'
                                    }`} />
                            </button>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                Update Message
                            </label>
                            <textarea
                                value={config.updateMessage}
                                onChange={(e) => setConfig(prev => ({ ...prev, updateMessage: e.target.value }))}
                                rows={2}
                                placeholder="Message shown when update is available..."
                                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl 
                                    focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 text-sm resize-none"
                            />
                        </div>
                    </div>
                </div>

                {/* APK Features Card */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                    <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-4">
                        <Zap size={18} className="text-emerald-500" />
                        APK Feature Flags
                    </h3>

                    <div className="space-y-3">
                        {[
                            { key: 'pushNotifications', icon: <Bell size={16} />, label: 'Push Notifications', desc: 'Study reminders & alerts' },
                            { key: 'hapticFeedback', icon: <Zap size={16} />, label: 'Haptic Feedback', desc: 'Vibration on interactions' },
                            { key: 'offlineMode', icon: <Globe size={16} />, label: 'Offline Mode', desc: 'Use without internet' },
                            { key: 'biometricAuth', icon: <Shield size={16} />, label: 'Biometric Auth', desc: 'Fingerprint/Face login' },
                            { key: 'backgroundSync', icon: <RefreshCw size={16} />, label: 'Background Sync', desc: 'Sync data in background' },
                            { key: 'deepLinking', icon: <Link size={16} />, label: 'Deep Linking', desc: 'Open app from links' },
                        ].map((feature) => (
                            <div
                                key={feature.key}
                                className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-slate-500">{feature.icon}</span>
                                    <div>
                                        <p className="font-medium text-slate-900 text-sm">{feature.label}</p>
                                        <p className="text-xs text-slate-500">{feature.desc}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setConfig(prev => ({
                                        ...prev,
                                        features: {
                                            ...prev.features,
                                            [feature.key]: !prev.features[feature.key as keyof typeof prev.features],
                                        },
                                    }))}
                                    className={`relative w-10 h-6 rounded-full transition-colors ${config.features[feature.key as keyof typeof config.features]
                                        ? 'bg-emerald-500'
                                        : 'bg-slate-300'
                                        }`}
                                >
                                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${config.features[feature.key as keyof typeof config.features]
                                        ? 'translate-x-4'
                                        : 'translate-x-0.5'
                                        }`} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Changelog Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-4">
                    <FileText size={18} className="text-indigo-500" />
                    Changelog / What's New
                </h3>

                <div className="space-y-4">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={newChangelogItem}
                            onChange={(e) => setNewChangelogItem(e.target.value)}
                            placeholder="Add a changelog item..."
                            onKeyDown={(e) => e.key === 'Enter' && addChangelogItem()}
                            className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl 
                                focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm"
                        />
                        <button
                            onClick={addChangelogItem}
                            className="px-4 py-2.5 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 transition-colors"
                        >
                            Add
                        </button>
                    </div>

                    <div className="space-y-2">
                        {config.changelog.map((item, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between p-3 bg-slate-50 rounded-xl group"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">
                                        {index + 1}
                                    </span>
                                    <span className="text-sm text-slate-700">{item}</span>
                                </div>
                                <button
                                    onClick={() => removeChangelogItem(index)}
                                    className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Preview */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 text-white">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                    <Smartphone size={18} />
                    APK Info Preview
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white/10 rounded-xl p-3">
                        <p className="text-xs text-slate-400">Version</p>
                        <p className="font-bold">{config.version}</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-3">
                        <p className="text-xs text-slate-400">Build</p>
                        <p className="font-bold">{config.versionCode}</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-3">
                        <p className="text-xs text-slate-400">Size</p>
                        <p className="font-bold">{config.fileSize}</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-3">
                        <p className="text-xs text-slate-400">Status</p>
                        <p className={`font-bold ${config.isEnabled ? 'text-emerald-400' : 'text-red-400'}`}>
                            {config.isEnabled ? 'Enabled' : 'Disabled'}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default APKConfigPanel;

// Helper function to get APK config from localStorage (bundled at build time)
export function getAPKConfig(): APKConfig {
    try {
        const saved = localStorage.getItem('luca_apk_config');
        if (saved) {
            return { ...DEFAULT_APK_CONFIG, ...JSON.parse(saved) };
        }
    } catch (e) {
        console.error('[APKConfig] Failed to load config');
    }
    return DEFAULT_APK_CONFIG;
}

/**
 * Fetch APK config from Firebase (LIVE - for APK users)
 * This allows APK to follow admin config changes without needing an update
 * Uses cache with 5-minute expiry for performance
 */
export async function getAPKConfigFromFirebase(): Promise<APKConfig> {
    try {
        const CACHE_KEY = 'luca_apk_config_firebase';
        const CACHE_EXPIRY_KEY = 'luca_apk_config_firebase_expiry';
        const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

        // Check cache first
        const cacheExpiry = localStorage.getItem(CACHE_EXPIRY_KEY);
        const cachedConfig = localStorage.getItem(CACHE_KEY);

        if (cacheExpiry && cachedConfig && Date.now() < parseInt(cacheExpiry)) {
            console.log('[APKConfig] Using cached Firebase config');
            return { ...DEFAULT_APK_CONFIG, ...JSON.parse(cachedConfig) };
        }

        console.log('[APKConfig] Fetching live config from Firebase...');

        // Fetch from Firebase REST API with cache-busting
        const cacheBuster = `?t=${Date.now()}`;
        const response = await fetch(
            `https://firestore.googleapis.com/v1/projects/luca-dffcd/databases/(default)/documents/config/apkVersion${cacheBuster}`,
            {
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache'
                }
            }
        );

        if (!response.ok) {
            console.log('[APKConfig] Firebase fetch failed, using local config');
            return getAPKConfig();
        }

        const data = await response.json();
        const fields = data.fields || {};

        // Parse Firebase config
        const firebaseConfig: Partial<APKConfig> = {
            version: fields.version?.stringValue,
            versionCode: fields.versionCode?.integerValue ? parseInt(fields.versionCode.integerValue) : undefined,
            downloadUrl: fields.downloadUrl?.stringValue,
            forceUpdate: fields.forceUpdate?.booleanValue || false,
            updateMessage: fields.updateMessage?.stringValue,
            releaseDate: fields.releaseDate?.stringValue,
            fileSize: fields.fileSize?.stringValue,
            changelog: (fields.changelog?.arrayValue?.values || []).map((v: any) => v.stringValue),
            isEnabled: fields.isEnabled?.booleanValue !== false, // Default true
        };

        // Merge with defaults
        const mergedConfig = { ...DEFAULT_APK_CONFIG, ...firebaseConfig };

        // Cache the result
        localStorage.setItem(CACHE_KEY, JSON.stringify(firebaseConfig));
        localStorage.setItem(CACHE_EXPIRY_KEY, (Date.now() + CACHE_DURATION).toString());

        console.log('[APKConfig] ✅ Fetched live config from Firebase:', mergedConfig.version);
        return mergedConfig;
    } catch (error) {
        console.error('[APKConfig] Firebase fetch error:', error);
        return getAPKConfig();
    }
}

/**
 * Force clear Firebase config cache
 * Call this when admin saves new config
 */
export function clearFirebaseConfigCache(): void {
    localStorage.removeItem('luca_apk_config_firebase');
    localStorage.removeItem('luca_apk_config_firebase_expiry');
    console.log('[APKConfig] Firebase config cache cleared');
}

/**
 * Clear all application caches
 * Useful for force-refreshing the APK when issues occur
 */
export async function clearAllCaches(): Promise<void> {
    try {
        console.log('[CacheBuster] Starting full cache clear...');

        // 1. Clear Service Worker caches
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
            console.log('[CacheBuster] Service Worker caches cleared:', cacheNames.length);
        }

        // 2. Clear Firebase config cache
        clearFirebaseConfigCache();

        // 3. Clear APK update cache
        localStorage.removeItem('luca_last_update_check');
        localStorage.removeItem('luca_update_dismissed');
        localStorage.removeItem('luca_apk_build_hash');
        localStorage.removeItem('luca_wasmer_etag');

        // 4. Unregister and re-register service workers
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
                await registration.unregister();
            }
            console.log('[CacheBuster] Service workers unregistered:', registrations.length);
        }

        console.log('[CacheBuster] ✅ All caches cleared successfully');
    } catch (error) {
        console.error('[CacheBuster] Cache clear error:', error);
    }
}

/**
 * Perform cache-busted refresh
 * Reloads the app with fresh resources
 */
export function performCacheBustedRefresh(): void {
    // Add timestamp to force bypass all caches
    const url = new URL(window.location.href);
    url.searchParams.set('_cb', Date.now().toString());
    window.location.href = url.toString();
}
